import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/utils';
import { getAccountCalls } from '../api';
import type { CallDetail } from '../types';

interface EngagementsTabProps {
  accountId: string;
}

const PAGE_SIZE = 10;

function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function EngagementsTab({ accountId }: EngagementsTabProps) {
  const [page, setPage] = useState(0);
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');
  const [directionFilter, setDirectionFilter] = useState<string>('all');
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-calls', accountId, page, outcomeFilter, directionFilter],
    queryFn: () =>
      getAccountCalls(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        outcome: outcomeFilter === 'all' ? undefined : outcomeFilter,
        direction: directionFilter === 'all' ? undefined : directionFilter,
      }),
  });

  if (isLoading) {
    return <EngagementsTabSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading engagements</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const calls = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Engagements</CardTitle>
            <CardDescription>
              {total} engagement{total !== 1 ? 's' : ''} found
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={outcomeFilter} onValueChange={(v) => { setOutcomeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outcomes</SelectItem>
                <SelectItem value="Connected">Connected</SelectItem>
                <SelectItem value="No answer">No answer</SelectItem>
                <SelectItem value="Left voicemail">Left voicemail</SelectItem>
                <SelectItem value="Left live message">Left live message</SelectItem>
                <SelectItem value="Wrong number">Wrong number</SelectItem>
                <SelectItem value="Busy">Busy</SelectItem>
              </SelectContent>
            </Select>
            <Select value={directionFilter} onValueChange={(v) => { setDirectionFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Directions</SelectItem>
                <SelectItem value="INBOUND">Inbound</SelectItem>
                <SelectItem value="OUTBOUND">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {calls.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No engagements found
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((call) => (
                  <EngagementRow
                    key={call.id}
                    call={call}
                    isExpanded={expandedCallId === call.id}
                    onToggle={() => setExpandedCallId(expandedCallId === call.id ? null : call.id)}
                  />
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EngagementRow({ call, isExpanded, onToggle }: { call: CallDetail; isExpanded: boolean; onToggle: () => void }) {
  const outcomeVariantMap: Record<string, 'success' | 'warning' | 'secondary' | 'destructive' | 'outline'> = {
    'Connected': 'success',
    'No answer': 'warning',
    'Left voicemail': 'secondary',
    'Left live message': 'secondary',
    'Wrong number': 'destructive',
    'Busy': 'warning',
  };
  const outcomeVariant = call.outcome ? (outcomeVariantMap[call.outcome] || 'outline') : 'outline';
  const hasContent = !!(call.body || call.callSummary || call.transcript || call.recordingUrl);

  return (
    <>
      <TableRow
        className={hasContent ? 'cursor-pointer hover:bg-muted/50' : ''}
        onClick={hasContent ? onToggle : undefined}
      >
        <TableCell className="whitespace-nowrap">
          {formatDate(call.callTimestamp)}
        </TableCell>
        <TableCell className="font-medium max-w-[200px] truncate">
          {call.title || 'Call'}
        </TableCell>
        <TableCell className="max-w-[150px] truncate">
          {call.ownerName || '—'}
        </TableCell>
        <TableCell className="max-w-[150px] truncate">
          {call.contactName || '—'}
        </TableCell>
        <TableCell>
          {call.direction && (
            <Badge variant="outline">
              {call.direction === 'INBOUND' ? 'Inbound' : 'Outbound'}
            </Badge>
          )}
        </TableCell>
        <TableCell>
          {call.outcome ? (
            <Badge variant={outcomeVariant}>{call.outcome}</Badge>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          {formatDuration(call.durationMs)}
        </TableCell>
        <TableCell>
          {hasContent ? (
            <div className="flex gap-1">
              {call.body && <Badge variant="outline" className="text-xs">Notes</Badge>}
              {call.callSummary && <Badge variant="outline" className="text-xs">AI Summary</Badge>}
              {call.transcript && <Badge variant="outline" className="text-xs">Transcript</Badge>}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/30 p-0">
            <CallDetailPanel call={call} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CallDetailPanel({ call }: { call: CallDetail }) {
  return (
    <div className="px-6 py-4 space-y-4">
      {/* Metadata row */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
        {call.ownerName && <span>{call.ownerName}</span>}
        {call.contactName && <span>Contact: {call.contactName}</span>}
        {call.dealName && <span>Deal: {call.dealName}</span>}
        {call.fromNumber && <span>From: {call.fromNumber}</span>}
        {call.toNumber && <span>To: {call.toNumber}</span>}
      </div>

      {/* Recording link */}
      {call.recordingUrl && (
        <div>
          <a
            href={call.recordingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            Open Recording
          </a>
        </div>
      )}

      {/* Notes section */}
      {call.body && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Notes</p>
          <div
            className="text-sm bg-background rounded-md border p-3 [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:mt-3 [&_h5]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_li]:my-0.5 [&_b]:font-semibold [&_hr]:my-3 [&_hr]:border-border"
            dangerouslySetInnerHTML={{ __html: call.body }}
          />
        </div>
      )}

      {/* AI Summary section */}
      {call.callSummary && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase mb-1">AI Summary</p>
          <div
            className="text-sm bg-background rounded-md border p-3 [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:mt-3 [&_h5]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_li]:my-0.5 [&_b]:font-semibold [&_hr]:my-3 [&_hr]:border-border"
            dangerouslySetInnerHTML={{ __html: call.callSummary }}
          />
        </div>
      )}

      {/* Transcript section */}
      {call.transcript && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-medium text-muted-foreground uppercase">Transcript</p>
            {call.transcriptionLanguage && (
              <Badge variant="outline" className="text-xs">
                {call.transcriptionLanguage}
              </Badge>
            )}
          </div>
          <div
            className="text-sm bg-background rounded-md border p-3 [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:mt-3 [&_h5]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_li]:my-0.5 [&_b]:font-semibold [&_hr]:my-3 [&_hr]:border-border"
            dangerouslySetInnerHTML={{ __html: call.transcript }}
          />
        </div>
      )}
    </div>
  );
}

function EngagementsTabSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-24 mt-1" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
